// @ts-check
// app-facade.js — Tidy top-level namespace for external consumers.
//
// The app exposes many loose `window.*` globals (NoteStorage, CryptoEngine,
// KeyStorage, CryptoStorage, DevicePairing, BinaryUtil, sanitizeHtml, etc.)
// because all modules load into the same window via <script defer> tags.
//
// This file collects them under a single `window.NotesApp` object so third
// parties (e-2-e tests, dev tools, userscripts) have one stable handle.  The
// existing globals are intentionally left in place — they're referenced from
// dozens of call sites across the codebase and removing them would be a huge
// invasive refactor that this review branch isn't scoped for.
//
// Loaded last so every module has already attached its own globals.
//
// De-windowing note: the facade no longer reads de-windowable symbols off
// `window[name]`. Anything that is a real ESM export (the encryption classes,
// the workspace singletons, the UI feedback helpers, and the pure utils) is
// now reached via a direct ESM `import` from its defining module. Only the
// genuine keep-surface stays on `liveProp`:
// `NoteStorage` (mutably swapped when PowerSync attaches or crypto wraps it),
// `PowerSyncNoteStorage`, `window._encryption`, and the editor/render globals
// that are not ESM exports. app-facade is only side-effect-imported by
// main.js, and none of the modules imported below import app-facade back, so
// these imports introduce no cycle.

import { CryptoEngine } from '../encryption/crypto-engine.js';
import { CryptoStorage } from '../encryption/crypto-storage.js';
import { DevicePairing } from '../encryption/device-pairing.js';
import { KeyStorage } from '../encryption/key-storage.js';
import { WorkspaceKeys } from '../encryption/workspace-keys.js';
// Pure utils. BinaryUtil / IDBUtil / DateUtil / SearchPredicate were exposed
// as window namespace objects bundling several functions; the modules export
// those functions individually, so the facade re-bundles them here.
import {
  arrayBufferToBase64,
  base64ToBlob,
  base64ToUint8,
  blobToBase64,
  uint8ToBase64,
} from '../utils/binary.js';
import {
  extractYYMMDD,
  formatTimestamp,
  getFormattedDate,
  parseYYMMDDToDate,
  startOfISOWeek,
  toYYMMDD,
} from '../utils/dates.js';
import { openDB, reqDone, txDone } from '../utils/idb-helpers.js';
import {
  escapeHtml,
  safeRenderMarkdown,
  safeRenderMarkdownInline,
  sanitizeHtml,
} from '../utils/sanitize.js';
import {
  createSearchPredicate,
  getTaskDotClass,
  getTaskScheduleStatus,
  makeNoteTermPredicate,
  makeTaskTermPredicate,
  normalizeQuotes,
  TASK_COLOR_STATUS,
} from '../utils/search-predicate.js';
import { InviteAcceptor } from '../workspaces/invite-acceptor.js';
import { WorkspaceManager } from '../workspaces/workspace-manager.js';
import { WorkspaceSessionVault } from '../workspaces/workspace-session-vault.js';
import { WorkspaceStorageWrapper } from '../workspaces/workspace-storage-wrapper.js';
// Multi-workspace singletons (each module exports a snapshot of its window.*
// shim taken at module-load time — app-facade loads last, so the shim is
// already installed and the import resolves to the live object).
import { WorkspaceStore } from '../workspaces/workspace-store.js';
import { WorkspaceSwitcher } from '../workspaces/workspace-switcher.js';
import { WorkspaceAvatar } from '../workspaces/workspace-toolbar-avatar.js';

(() => {
  // Live getters so the facade always reflects the current value of a global
  // (e.g. NoteStorage is swapped when PowerSync attaches or crypto wraps it).
  /**
   * @param {any} target
   * @param {string} name
   * @returns {void}
   */
  function liveProp(target: any, name: any) {
    Object.defineProperty(target, name, {
      enumerable: true,
      configurable: true,
      get() {
        return (window as any)[name];
      },
    });
  }

  /** @type {any} */
  const facade: any = {};

  // ── Keep-surface: genuine window globals that are NOT de-windowable ───────
  // NoteStorage is swapped at runtime (PowerSync / crypto wrap); the editor /
  // rendering globals below are not ESM exports (no module to import them from).
  const keepWindow = [
    // Storage layer
    'NoteStorage',
    'PowerSyncNoteStorage',
    // Editor / rendering (not ESM exports — stay on window)
    'AutoEditQueue',
    'PreviewRenderer',
    'Autocomplete',
  ];
  for (const name of keepWindow) liveProp(facade, name);

  // ── De-windowed: resolved through ESM imports instead of window reads ─────
  // Each binding is a module-singleton (a class, a snapshot-of-window object,
  // or a re-bundled namespace), so a plain getter returning the captured value
  // matches the previous live-window behaviour.
  const SearchPredicate = {
    createSearchPredicate,
    makeNoteTermPredicate,
    makeTaskTermPredicate,
    getTaskScheduleStatus,
    getTaskDotClass,
    normalizeQuotes,
    TASK_COLOR_STATUS,
  };
  const BinaryUtil = {
    uint8ToBase64,
    base64ToUint8,
    arrayBufferToBase64,
    base64ToBlob,
    blobToBase64,
  };
  const IDBUtil = { openDB, reqDone, txDone };
  const DateUtil = {
    toYYMMDD,
    getFormattedDate,
    formatTimestamp,
    parseYYMMDDToDate,
    extractYYMMDD,
    startOfISOWeek,
  };

  const imported = {
    // Encryption classes
    CryptoEngine,
    CryptoStorage,
    KeyStorage,
    DevicePairing,
    WorkspaceKeys,
    // Multi-workspace singletons
    WorkspaceStore,
    WorkspaceSwitcher,
    WorkspaceSessionVault,
    WorkspaceStorageWrapper,
    WorkspaceAvatar,
    WorkspaceManager,
    InviteAcceptor,
    // Utilities
    BinaryUtil,
    IDBUtil,
    DateUtil,
    SearchPredicate,
    sanitizeHtml,
    escapeHtml,
    safeRenderMarkdown,
    safeRenderMarkdownInline,
  };
  for (const [name, value] of Object.entries(imported)) {
    Object.defineProperty(facade, name, {
      enumerable: true,
      configurable: true,
      get() {
        return value;
      },
    });
  }

  // Encryption state is mutable — expose via getter so the facade always
  // reflects the current key / userId / active flags.
  Object.defineProperty(facade, 'encryption', {
    enumerable: true,
    configurable: true,
    get() {
      return window._encryption;
    },
  });

  // Semver-style version tag for consumers that want to feature-detect.
  facade.version = '1.0.0';

  window.NotesApp = facade;
})();

// Phase 4.6: ES-module export. The IIFE above keeps window.NotesApp
// for legacy callers; this re-exposes the same handle as a named
// import. window.NotesApp tail removed in 4.7 (or kept as the stable
// external surface — the audit hadn't decided yet).
export const NotesApp = window.NotesApp;
